import type {
  AuthenticationResponseJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";
import type {
  AuthenticationOptionsResponse,
  AuthSessionResponse,
  AuthSuccessResponse,
  AuthUser,
  EmailAccessCodeRequestResponse,
  InvitationCreatedResponse,
  InvitationInfo,
  InviteUserInput,
  PasskeyCreatedResponse,
  PasskeyDeviceLinkResponse,
  PasskeyListResponse,
  RegistrationOptionsResponse,
  UserRole,
  UserStatus,
} from "../shared/auth";
import { requestJson } from "./api";

export function fetchSession() {
  return requestJson<AuthSessionResponse>("/api/auth/session");
}

export function fetchInvitation(token: string) {
  return requestJson<InvitationInfo>(
    `/api/auth/invitations/${encodeURIComponent(token)}`,
  );
}

export function beginPasskeyRegistration(invitationToken: string) {
  return requestJson<RegistrationOptionsResponse>(
    "/api/auth/passkeys/register/options",
    {
      method: "POST",
      body: JSON.stringify({ invitationToken }),
    },
  );
}

export function finishPasskeyRegistration(input: {
  invitationToken: string;
  challengeId: string;
  credentialName: string;
  response: RegistrationResponseJSON;
}) {
  return requestJson<AuthSuccessResponse>(
    "/api/auth/passkeys/register/verify",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export function fetchMyPasskeys() {
  return requestJson<PasskeyListResponse>("/api/auth/passkeys");
}

export function deleteMyPasskey(credentialId: string) {
  return requestJson<{ ok: true }>("/api/auth/passkeys", {
    method: "DELETE",
    body: JSON.stringify({ credentialId }),
  });
}

export function beginAdditionalPasskeyRegistration() {
  return requestJson<RegistrationOptionsResponse>(
    "/api/auth/passkeys/self/options",
    { method: "POST" },
  );
}

export function finishAdditionalPasskeyRegistration(input: {
  challengeId: string;
  credentialName: string;
  response: RegistrationResponseJSON;
}) {
  return requestJson<PasskeyCreatedResponse>(
    "/api/auth/passkeys/self/verify",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export function createPasskeyDeviceLink() {
  return requestJson<PasskeyDeviceLinkResponse>(
    "/api/auth/passkeys/device-links",
    { method: "POST" },
  );
}

export function fetchPasskeyDeviceLink(token: string) {
  return requestJson<InvitationInfo>(
    `/api/auth/passkeys/device-links/${encodeURIComponent(token)}`,
  );
}

export function beginPasskeyDeviceLinkRegistration(deviceLinkToken: string) {
  return requestJson<RegistrationOptionsResponse>(
    "/api/auth/passkeys/device-links/options",
    {
      method: "POST",
      body: JSON.stringify({ deviceLinkToken }),
    },
  );
}

export function finishPasskeyDeviceLinkRegistration(input: {
  deviceLinkToken: string;
  challengeId: string;
  credentialName: string;
  response: RegistrationResponseJSON;
}) {
  return requestJson<AuthSuccessResponse>(
    "/api/auth/passkeys/device-links/verify",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export function beginPasskeyLogin() {
  return requestJson<AuthenticationOptionsResponse>(
    "/api/auth/passkeys/login/options",
    { method: "POST" },
  );
}

export function finishPasskeyLogin(input: {
  challengeId: string;
  response: AuthenticationResponseJSON;
}) {
  return requestJson<AuthSuccessResponse>("/api/auth/passkeys/login/verify", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function requestEmailAccessCode(input: { email: string }) {
  return requestJson<EmailAccessCodeRequestResponse>(
    "/api/auth/email-code/request",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export function verifyEmailAccessCode(input: { email: string; code: string }) {
  return requestJson<AuthSuccessResponse>("/api/auth/email-code/verify", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function logoutSession() {
  return requestJson<{ ok: true }>("/api/auth/logout", { method: "POST" });
}

export function fetchUsers() {
  return requestJson<{ users: AuthUser[] }>("/api/admin/users");
}

export function inviteUser(input: InviteUserInput) {
  return requestJson<InvitationCreatedResponse>("/api/admin/users", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateUser(
  userId: string,
  input: { role?: UserRole; status?: UserStatus },
) {
  return requestJson<{ user: AuthUser }>(
    `/api/admin/users/${encodeURIComponent(userId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
}

export function renewInvitation(userId: string) {
  return requestJson<InvitationCreatedResponse>(
    `/api/admin/users/${encodeURIComponent(userId)}/invitation`,
    { method: "POST" },
  );
}
